// src/lib/managers/dataManager/google/sent-search-logic.ts

/**
 * Lecture des mails envoyés avec des critères (étape `lireMails`, boîte « envoyes », du contrat par
 * étapes de l'assistant) : la requête Gmail. Fonction pure ; les appels sont dans
 * GoogleManager.searchSentMailCopyRefs.
 */

import { MailFolder } from "../graph/types/graph";
import type { SentMailsCriteria } from "../IDataApi";
import { mapFolderToLabel } from "./types/const";

/** Pages de 100 lues au plus pour une lecture « les plus anciens d'abord » (Gmail rend les plus récents d'abord). */
export const SENT_OLDEST_FIRST_MAX_PAGES = 10;

const quoted = (value: string) => `"${value.replace(/"/g, '\\"')}"`;

/** Secondes depuis 1970 : Gmail lit une date seule à minuit, heure du Pacifique. */
const seconds = (date: string | null): number | null => {
    const time = date ? new Date(date).getTime() : NaN;
    return Number.isNaN(time) ? null : Math.floor(time / 1000);
};

/**
 * Requête Gmail des mails envoyés qui répondent aux critères : `in:sent`, un destinataire parmi ceux
 * demandés (à, cc ou cci), un mot-clé parmi ceux demandés, la période, avec ou sans pièce jointe.
 */
export function sentMailsQuery(criteria: SentMailsCriteria): string {
    const parts = [`in:${mapFolderToLabel[MailFolder.SentItems]}`];
    if (criteria.recipients.length > 0) {
        const conditions = criteria.recipients.flatMap((recipient) =>
            ["to", "cc", "bcc"].map((field) => `${field}:${quoted(recipient)}`),
        );
        parts.push(`(${conditions.join(" OR ")})`);
    }
    if (criteria.keywords.length > 0) {
        parts.push(`(${criteria.keywords.map(quoted).join(" OR ")})`);
    }
    const start = seconds(criteria.startDate);
    if (start !== null) parts.push(`after:${start}`);
    const end = seconds(criteria.endDate);
    if (end !== null) parts.push(`before:${end}`);
    if (criteria.hasAttachment !== undefined) {
        parts.push(
            criteria.hasAttachment ? "has:attachment" : "-has:attachment",
        );
    }
    return parts.join(" ");
}
